import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';

import { ReplaySubject, takeUntil } from 'rxjs';

import { StorageAPIService } from 'app/providers/storage-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';

import { SystemService } from 'app/services/system.service';
import { Subscribable } from 'app/utils/subscrible';
import { WrappedRequest } from '../api.service';
import { SemantriaAccount } from '../bridge-api/account.service';
import { GroupService } from '../semantria-api/group.service';
import { SemantriaUser } from '../semantria-api/semantria-users.service';
import { Field, FieldService } from './field.service';

export interface AccountMetadata {
  metadata: Array<Field>;
  configurationMap?: { [key: string]: string[] };
}

export interface AccountCounts {
  documentLimit: number;
  documentCount: number;
  semantriaCredits: number;
  projectsCount: number;
  projectsLimit: number;
}

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class StorageAccountService extends StorageAPIService implements OnDestroy {
  public groupFieldsSub = new Subscribable<AccountMetadata>(
    this.getFieldsForCurrentGroup.bind(this)
  );
  public accountSub = new Subscribable<AccountMetadata>(this.getAccount.bind(this));
  private accountCountsRequest: WrappedRequest<AccountCounts>;

  private unsub$ = new ReplaySubject<void>(1);
  private semantriaAccount = inject(SemantriaAccount, { optional: true });

  constructor(
    http: HttpClient,
    systemService: SystemService,
    private fieldService: FieldService,
    private groupService: GroupService
  ) {
    super(http, systemService);

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.unsub$))
      .subscribe(() => this.groupFieldsSub.clear());

    this.fieldService
      .onChange()
      .pipe(takeUntil(this.unsub$))
      .subscribe(() => this.groupFieldsSub.clear());
  }

  ngOnDestroy(): void {
    this.unsub$.next();
  }

  public clearAccountCache(): void {
    this.groupFieldsSub.clear();
    this.accountSub.clear();
    this.semantriaAccount?.sub.clear();
    this.accountCountsRequest = undefined;
  }

  public static mapAccountMetadata(account: AccountMetadata): AccountMetadata {
    return account;
  }

  public async getAccountCounts(): Promise<AccountCounts> {
    if (!this.accountCountsRequest?.isInProgress()) {
      const countsPromise = this.get<AccountCounts>('/account/counts');
      this.accountCountsRequest = new WrappedRequest(countsPromise);
    }
    return this.accountCountsRequest.request;
  }

  public async getFieldsForCurrentGroup(): Promise<AccountMetadata> {
    const result = await this.fieldService.getFields();
    return { metadata: result.items };
  }

  public async getAccount(): Promise<AccountMetadata> {
    return this.get<AccountMetadata>('/account');
  }

  public async hasInteractiveSmartSummaryResources(): Promise<boolean> {
    try {
      return await this.get<boolean>('/account/_hasSmartSummaryResources');
    } catch (error) {
      return false;
    }
  }

  public async deleteUser(id): Promise<SemantriaUser> {
    return this.delete(`/backend/users/${id}`);
  }
}
